import type {
  CreateMessageMutation,
  GetMessagesQuery,
  GetMessagesQueryVariables,
} from '#src/graphql/generated';
import type { ApolloCache } from '@apollo/client';

import { GET_MESSAGES } from '#src/graphql/queries/messages';

function byCreatedAtAsc(
  a: { createdAt: unknown },
  b: { createdAt: unknown },
): number {
  return (
    new Date(a.createdAt as string).getTime() -
    new Date(b.createdAt as string).getTime()
  );
}

/**
 * Inserts `createMessage` mutation result into the cached {@link GET_MESSAGES} result
 * for the given variables (must match the active `useQuery` for that view).
 */
export function mergeCreateMessageIntoGetMessagesCache(
  cache: ApolloCache,
  created: CreateMessageMutation['createMessage'],
  variables: GetMessagesQueryVariables,
): void {
  const createdWithReactions = {
    ...created,
    reactions:
      created.reactions != null &&
      typeof created.reactions === 'object' &&
      !Array.isArray(created.reactions)
        ? created.reactions
        : {},
  };

  cache.updateQuery<GetMessagesQuery>(
    { query: GET_MESSAGES, variables },
    (existing) => {
      if (!existing?.messages) return existing;

      if (createdWithReactions.parentMessageId) {
        const parentId = createdWithReactions.parentMessageId;
        return {
          messages: existing.messages.map((root) => {
            if (root.id !== parentId) return root;
            const existingChildren = root.childMessages ?? [];
            if (
              existingChildren.some((c) => c.id === createdWithReactions.id)
            ) {
              return root;
            }
            const childMessages = [
              ...existingChildren,
              createdWithReactions,
            ].sort(byCreatedAtAsc);
            return { ...root, childMessages };
          }),
        };
      }

      if (existing.messages.some((m) => m.id === createdWithReactions.id)) {
        return existing;
      }

      const messages = [...existing.messages, createdWithReactions].sort(
        byCreatedAtAsc,
      );
      return { ...existing, messages };
    },
  );
}
